import { combineReducers, configureStore, AnyAction } from "@reduxjs/toolkit";

import { userAuthSlice, clearAuth } from "./slice/userAuthSlice.tsx";
import { alertSlice } from "./slice/alertSlice.tsx";
import { brandSlice } from "./slice/brandSlice.tsx";
import { profileSlice } from "./slice/profileSlice.tsx";
import { personasSlice } from "./slice/personasSlice.tsx";
import { campaignsSlice } from "./slice/campaignsSlice.tsx";
import { creatorBriefsSlice } from "./slice/creatorBriefsSlice.tsx";
import { creatorsSlice } from "./slice/creatorsSlice.tsx";
import { companySlice } from "./slice/companySlice.tsx";
import { helpSlice } from "./slice/helpSlice.tsx";
import { brandAssetsSlice } from "./slice/brandAssetsSlice.tsx";
import { signOutUser } from "./thunks/userAuthThunk.tsx";
import { discoverCommunitiesSlice } from "./slice/discoverCommunitiesSlice.tsx";
import { onboardingSlice } from "./slice/onboardingSlice.tsx";
import { brandSocialHandlesSlice } from "./slice/brandSocialHandlesSlice.tsx";
import { personaGroupsSlice } from "./slice/personaGroupsSlice.tsx";
import { organizationsSlice } from "./slice/organizationsSlice.tsx";

const appReducer = combineReducers({
  userAuth: userAuthSlice.reducer,
  alert: alertSlice.reducer,
  brand: brandSlice.reducer,
  profile: profileSlice.reducer,
  personas: personasSlice.reducer,
  campaigns: campaignsSlice.reducer,
  company: companySlice.reducer,
  creators: creatorsSlice.reducer,
  help: helpSlice.reducer,
  creatorBriefs: creatorBriefsSlice.reducer,
  brandAssets: brandAssetsSlice.reducer,
  discoverCommunities: discoverCommunitiesSlice.reducer,
  onboarding: onboardingSlice.reducer,
  brandSocialHandles: brandSocialHandlesSlice.reducer,
  personaGroups: personaGroupsSlice.reducer,
  organizations: organizationsSlice.reducer,
});

/**
 * Represents the root state of the application's Redux store, which combines all
 * the reducers into a single overarching state tree. It is determined by the
 * structure defined in the `appReducer` function.
 */
export type RootState = ReturnType<typeof appReducer>;

/**
 * Combines all reducers to create a root reducer for the application.
 *
 * This root reducer handles all actions dispatched to the Redux store.
 * It delegates the handling of state changes to individual reducers combined inside `appReducer`.
 * Additionally, it includes a specific case for handling user logout actions.
 *
 * When a logout action is dispatched, the `rootReducer` resets the application's state
 * by reinitializing it from the combined initial states of all slice reducers.
 * For any other actions, it delegates processing to `appReducer`.
 *
 * @param {RootState | undefined} state - The current state of the Redux store, or `undefined` for the initial state.
 * @param {AnyAction} action - The action dispatched to the reducer.
 * @returns {RootState} The updated state after processing the action.
 */
const rootReducer = (state: RootState | undefined, action: AnyAction) => {
  // If the action is a logout or a failed bootstrap, reset the entire state.
  if (signOutUser.fulfilled.match(action) || clearAuth.match(action)) {
    // Re-create the state from the initial state of all slices.
    return appReducer(undefined, action);
  }

  // Otherwise pass the action to the app reducer
  return appReducer(state, action);
};

export const store = configureStore({
  reducer: rootReducer,
});

export type AppDispatch = typeof store.dispatch;
